import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
    TextInput,
    PasswordInput,
    Select,
    Button,
    Badge,
    Alert,
    Table,
    ActionIcon,
    Autocomplete
} from '@mantine/core';
import {
    IconAlertCircle,
    IconCheck,
    IconReceiptTax,
    IconTrash,
    IconEdit,
    IconRefresh,
    IconInfoCircle,
    IconPlus
} from '@tabler/icons-react';
import { openConfirmModal } from '@mantine/modals';
import SettingsLayout from '@/components/settings-layout';
import SettingsCard from '@/components/settings-card';
import { localTenant, updateMe } from '@/services/session-service';
import { setWebTitle } from '@/lib/format-utils';
import { useUpdateTenant, useCreateAfipCert, useAuthAfipService, useCreateAfipSalesPoint, useSyncAfipSalesPoints } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import CardModal from '@/components/card-modal';

import { SalesPointItem } from '@turizi/shared';
export type { SalesPointItem };

export const getInitialSalesPoints = (afip: any): SalesPointItem[] => {
    if (Array.isArray(afip?.sales_points)) {
        return afip.sales_points;
    }
    if (afip?.pos_number) {
        return [{
            id: String(afip.pos_number),
            number: String(afip.pos_number),
            name: afip.pos_name || `POS ${afip.pos_number}`,
            system: afip.pos_system || '',
            origin: 'user' as const,
            address: afip.pos_address || afip.address || '',
        }];
    }
    return [];
};

export const isWebServicePos = (system?: string): boolean => {
    if (!system) return false;
    const s = system.toLowerCase();
    return (
        s.includes('web service') ||
        s.includes('web services') ||
        s.includes('webservices') ||
        s === 'raw' ||
        s === 'maw' ||
        s === 'feews' ||
        s.includes('wsfe') ||
        s.includes('ws')
    );
};

export default function SettingsTaxSettingsPage(): JSX.Element {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const country = searchParams.get('c') || 'Argentina';

    const tenant = localTenant();
    const updateTenantMutation = useUpdateTenant();
    const createCertMutation = useCreateAfipCert();
    const authServiceMutation = useAuthAfipService();
    const createSalesPointMutation = useCreateAfipSalesPoint();
    const syncSalesPointsMutation = useSyncAfipSalesPoints();

    // Load initial values from settings.afip
    const afipSettings = tenant?.settings?.afip || {};
    const [cuit, setCuit] = useState(afipSettings.cuit || '');
    const [username, setUsername] = useState(afipSettings.username || '');
    const [password, setPassword] = useState(afipSettings.password || '');
    const [alias, setAlias] = useState(afipSettings.alias || 'turizi');
    const [environment, setEnvironment] = useState(afipSettings.environment || 'development');
    const [errorData, setErrorData] = useState<any>(null);
    const [errorModalOpened, setErrorModalOpened] = useState(false);
    const [certModalOpened, setCertModalOpened] = useState(false);

    const [salesPoints, setSalesPoints] = useState<SalesPointItem[]>(() => getInitialSalesPoints(afipSettings));

    // Modal state for Sales Points
    const [salesPointModalOpened, setSalesPointModalOpened] = useState(false);
    const [editingPos, setEditingPos] = useState<SalesPointItem | null>(null);
    const [modalPosNumber, setModalPosNumber] = useState('');
    const [modalPosName, setModalPosName] = useState('');
    const [modalPosSystem, setModalPosSystem] = useState('MAW');
    const [modalPosAddress, setModalPosAddress] = useState('');

    const availableAddresses = React.useMemo(() => {
        const list: string[] = [];
        if (afipSettings.all_sales_points && Array.isArray(afipSettings.all_sales_points)) {
            afipSettings.all_sales_points.forEach((p: any) => {
                const addr = String(p.address || p.domicilio || '').trim();
                if (addr && !list.includes(addr)) {
                    list.push(addr);
                }
            });
        }
        if (salesPoints && Array.isArray(salesPoints)) {
            salesPoints.forEach((p) => {
                const addr = String(p.address || '').trim();
                if (addr && !list.includes(addr)) {
                    list.push(addr);
                }
            });
        }
        const tenantAddr = String(afipSettings.address || tenant?.settings?.address || '').trim();
        if (tenantAddr && !list.includes(tenantAddr)) {
            list.push(tenantAddr);
        }
        return list;
    }, [afipSettings.all_sales_points, afipSettings.address, salesPoints, tenant?.settings?.address]);

    setWebTitle(t('tax_settings'));

    const handleCopyCert = () => {
        if (afipSettings.cert) {
            navigator.clipboard.writeText(afipSettings.cert);
            notify.success(t('certificate_copied_success'), t('success'));
        }
    };

    const getDraftData = () => ({
        cuit: cuit,
        username: username,
        password: password,
        alias: alias,
        environment: environment,
    });

    const validateForm = () => {
        if (!cuit || !username || !password || !alias) {
            notify.error(t('please_select_all_fields'), t('error'));
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!tenant) return;

        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                afip: getDraftData()
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('save_settings_success', { defaultValue: 'Settings saved successfully' }), t('success'));
            },
            onError: () => {
                notify.error(t('error_updating_settings', { defaultValue: 'Error updating settings' }), t('error'));
            }
        });
    };

    const handleGenerateCertificate = () => {
        if (!tenant || !validateForm()) return;

        createCertMutation.mutate({
            tenantId: tenant.id,
            data: getDraftData()
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('certificate_generated_success'), t('success'));
            },
            onError: (err: any) => {
                const msg = err.response?.data?.message || err.message || t('error_generating_certificate');
                notify.error(msg, t('error'));
                if (err.response?.data) {
                    setErrorData(err.response.data);
                    setErrorModalOpened(true);
                }
            }
        });
    };

    const handleAuthorizeService = () => {
        if (!tenant || !validateForm()) return;

        authServiceMutation.mutate({
            tenantId: tenant.id,
            data: getDraftData()
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('service_authorized_success'), t('success'));
            },
            onError: (err: any) => {
                const msg = err.response?.data?.message || err.message || t('error_authorizing_service');
                notify.error(msg, t('error'));
                if (err.response?.data) {
                    setErrorData(err.response.data);
                    setErrorModalOpened(true);
                }
            }
        });
    };

    const persistSalesPoints = (updatedPoints: SalesPointItem[]) => {
        if (!tenant) return;
        setSalesPoints(updatedPoints);

        const primaryPos = updatedPoints[0];

        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                afip: {
                    sales_points: updatedPoints,
                    pos_number: primaryPos ? primaryPos.number : null,
                    pos_name: primaryPos ? primaryPos.name : null,
                    pos_system: primaryPos ? (primaryPos.system || null) : null,
                    sales_point_created: updatedPoints.length > 0,
                }
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('save_settings_success'), t('success'));
            },
            onError: () => {
                notify.error(t('error_updating_settings'), t('error'));
            }
        });
    };

    const handleImportAfipPoint = (afipPoint: any) => {
        if (!isWebServicePos(afipPoint.system)) {
            notify.error(t('requires_web_services'), t('error'));
            return;
        }

        const numStr = String(afipPoint.number).trim();
        if (salesPoints.some(sp => sp.number === numStr)) {
            return;
        }

        const newPoint: SalesPointItem = {
            id: String(Date.now()),
            number: numStr,
            name: afipPoint.name || afipPoint.displayName || '',
            system: afipPoint.system || '',
            origin: 'afip',
            address: afipPoint.address || '',
        };
        persistSalesPoints([...salesPoints, newPoint]);
    };

    const handleSyncAfipSalesPoints = () => {
        if (!tenant || !validateForm()) return;

        syncSalesPointsMutation.mutate({
            tenantId: tenant.id,
            data: getDraftData()
        }, {
            onSuccess: async () => {
                await updateMe(true);
                notify.success(t('sync_afip_sales_points_success'), t('success'));
            },
            onError: (err: any) => {
                const msg = err.response?.data?.message || err.message || t('sync_afip_sales_points_error');
                notify.error(msg, t('error'));
                if (err.response?.data) {
                    setErrorData(err.response.data);
                    setErrorModalOpened(true);
                }
            }
        });
    };

    const handleOpenEditSalesPoint = (pos: SalesPointItem) => {
        setEditingPos(pos);
        setModalPosNumber(pos.number);
        setModalPosName(pos.name);
        setModalPosSystem(pos.system || '');
        setModalPosAddress(pos.address || availableAddresses[0] || '');
        setSalesPointModalOpened(true);
    };

    const handleSaveSalesPointModal = () => {
        if (!editingPos) return;
        if (!modalPosName.trim()) {
            notify.error(t('please_select_all_fields'), t('error'));
            return;
        }

        const updatedPoints: SalesPointItem[] = salesPoints.map((p) => {
            const isMatch = (editingPos.id && p.id === editingPos.id) || p.number === editingPos.number;
            if (isMatch) {
                return {
                    ...p,
                    name: modalPosName.trim(),
                    address: modalPosAddress.trim() || undefined,
                };
            }
            return p;
        });

        setSalesPointModalOpened(false);
        setEditingPos(null);
        persistSalesPoints(updatedPoints);
    };

    const handleDeleteSalesPoint = (pos: SalesPointItem) => {
        openConfirmModal({
            title: t('delete_sales_point'),
            children: <div className="text-sm">{t('confirm_delete_sales_point')}</div>,
            labels: { confirm: t('delete'), cancel: t('cancel') },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                const updatedPoints = salesPoints.filter((p) => {
                    if (pos.id && p.id) return p.id !== pos.id;
                    return p.number !== pos.number;
                });
                persistSalesPoints(updatedPoints);
            }
        });
    };

    const handleOpenCreateSalesPoint = () => {
        setEditingPos(null);
        const hasMonotributo = afipSettings.all_sales_points?.some((p: any) =>
            (p.system || '').toLowerCase().includes('monotributo') || (p.system || '').includes('MAW')
        );
        setModalPosNumber('');
        setModalPosName(tenant?.name || '');
        setModalPosSystem(hasMonotributo ? 'MAW' : 'RAW');
        setModalPosAddress(availableAddresses[0] || '');
        setSalesPointModalOpened(true);
    };

    const handleCreateSalesPoint = () => {
        if (!tenant || !validateForm()) return;
        if (!modalPosName.trim()) {
            notify.error(t('please_select_all_fields'), t('error'));
            return;
        }

        const data = {
            ...getDraftData(),
            sistema: modalPosSystem,
            nombreFantasia: modalPosName.trim(),
            domicilio: modalPosAddress.trim() || undefined,
            address: modalPosAddress.trim() || undefined,
            autoConfigure: true
        };

        createSalesPointMutation.mutate({
            tenantId: tenant.id,
            data
        }, {
            onSuccess: async (res: any) => {
                const newPosNumber = String(res?.data?.posNumber || '1');
                const allSalesPoints = res?.data?.allSalesPoints || [];
                const pointFromAfip = allSalesPoints.find((p: any) => String(p.number) === newPosNumber) || res?.data?.createdPoint;

                const newSalesPoint: SalesPointItem = {
                    id: String(newPosNumber),
                    number: newPosNumber,
                    name: modalPosName.trim(),
                    system: pointFromAfip?.system || res?.data?.system || '',
                    origin: 'user',
                    address: modalPosAddress.trim() || pointFromAfip?.address || '',
                };

                const updatedPoints = [
                    ...salesPoints.filter(p => p.number !== newPosNumber),
                    newSalesPoint
                ];

                await updateMe(true);
                persistSalesPoints(updatedPoints);
                setSalesPointModalOpened(false);
                notify.success(t('sales_point_created_success'), t('success'));
            },
            onError: (err: any) => {
                const msg = err.response?.data?.message || err.message || t('error_creating_sales_point');
                notify.error(msg, t('error'));
                if (err.response?.data) {
                    setErrorData(err.response.data);
                    setErrorModalOpened(true);
                }
            }
        });
    };

    const isPending = updateTenantMutation.isPending || createCertMutation.isPending || authServiceMutation.isPending || createSalesPointMutation.isPending || syncSalesPointsMutation.isPending;

    return (
        <div className="panel-content max-w-5xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-semibold text-gray-900 tracking-tight mb-2">
                    {country}
                </h1>
            </div>

            {country === 'Argentina' ? (
                <SettingsLayout>
                    <div className="flex flex-col gap-6 w-full">
                        {/* Configuration Form Card */}
                        <SettingsCard
                            title={t('afip_arca_settings')}
                            description={t('afip_arca_settings_desc')}
                            onSave={handleSave}
                            saving={updateTenantMutation.isPending}
                            disabled={isPending}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Select
                                    label={t('environment')}
                                    value={environment}
                                    onChange={(value) => setEnvironment(value || 'development')}
                                    data={[
                                        { value: 'development', label: t('homologation') },
                                        { value: 'production', label: t('production') }
                                    ]}
                                    disabled={isPending}
                                    className="md:col-span-2 font-bold"
                                />

                                <TextInput
                                    label={t('cuit')}
                                    value={cuit}
                                    onChange={(e) => setCuit(e.target.value)}
                                    placeholder="e.g. 20111111112"
                                    disabled={isPending}
                                    required
                                />

                                <TextInput
                                    label={t('arca_username')}
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="ARCA Login CUIT"
                                    disabled={isPending}
                                    required
                                />

                                <PasswordInput
                                    label={t('arca_password')}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="ARCA Clave Fiscal"
                                    disabled={isPending}
                                    required
                                />

                                <TextInput
                                    label={t('alias')}
                                    value={alias}
                                    onChange={(e) => setAlias(e.target.value)}
                                    placeholder="Certificate alias"
                                    disabled={isPending}
                                    required
                                />
                            </div>
                        </SettingsCard>

                        {/* Integration Steps & Status Card */}
                        <div className="border border-gray-200 rounded-lg bg-white p-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
                                <IconReceiptTax className="text-blue-600" size={24} />
                                {t('integration_steps_title')}
                            </h3>
                            <p className="text-sm text-gray-500 mb-6">
                                {t('integration_steps_desc')}
                            </p>

                            <div className="flex flex-col gap-6 relative pl-8 before:content-[''] before:absolute before:left-4 before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-200">

                                {/* Step 1: Save Credentials */}
                                <div className="relative">
                                    <div className="absolute -left-[28px] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white shadow-sm ring-4 ring-white">
                                        1
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                                        <h4 className="font-semibold text-gray-900">{t('step_save_credentials')}</h4>
                                        <div>
                                            {afipSettings.cuit && afipSettings.username ? (
                                                <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>{t('saved')}</Badge>
                                            ) : (
                                                <Badge color="yellow" variant="light">{t('pending_config')}</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {t('step_save_credentials_desc')}
                                    </p>
                                </div>

                                {/* Step 2: Generate Certificate */}
                                <div className="relative">
                                    <div className={`absolute -left-[28px] top-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold shadow-sm ring-4 ring-white ${afipSettings.cert_generated ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                        2
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                                        <h4 className="font-semibold text-gray-900">{t('step_generate_certificate')}</h4>
                                        <div>
                                            {afipSettings.cert_generated ? (
                                                <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>{t('certificate_active')}</Badge>
                                            ) : (
                                                <Badge color="yellow" variant="light">{t('pending_generation')}</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-3">
                                        {t('step_generate_certificate_desc')}
                                    </p>
                                    <div className="flex gap-2 items-center">
                                        <Button
                                            variant="outline"
                                            color="blue"
                                            size="xs"
                                            onClick={handleGenerateCertificate}
                                            loading={createCertMutation.isPending}
                                            disabled={isPending}
                                        >
                                            {t('generate_certificate')}
                                        </Button>
                                        {afipSettings.cert_generated && afipSettings.cert && (
                                            <Button
                                                variant="light"
                                                color="gray"
                                                size="xs"
                                                onClick={() => setCertModalOpened(true)}
                                            >
                                                {t('view_certificate')}
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Step 3: Authorize Service */}
                                <div className="relative">
                                    <div className={`absolute -left-[28px] top-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold shadow-sm ring-4 ring-white ${afipSettings.service_authorized ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                        3
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                                        <h4 className="font-semibold text-gray-900">{t('step_authorize_service')}</h4>
                                        <div>
                                            {afipSettings.service_authorized ? (
                                                <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>{t('service_authorized_badge')}</Badge>
                                            ) : (
                                                <Badge color="yellow" variant="light">{t('pending_authorization')}</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-3">
                                        {t('step_authorize_service_desc')}
                                    </p>
                                    <Button
                                        variant="outline"
                                        color="green"
                                        size="xs"
                                        onClick={handleAuthorizeService}
                                        loading={authServiceMutation.isPending}
                                        disabled={isPending || !afipSettings.cert_generated}
                                    >
                                        {t('authorize_web_service')}
                                    </Button>
                                </div>
                            </div>

                            {afipSettings.last_error && (
                                <Alert icon={<IconAlertCircle size={16} />} title={t('error')} color="red" className="mt-6">
                                    {afipSettings.last_error}
                                </Alert>
                            )}
                        </div>

                        {/* Dedicated POS Management Section */}
                        <div className="border border-gray-200 rounded-lg bg-white p-6 shadow-sm space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
                                <div>
                                    <div className="flex items-center gap-2.5">
                                        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                            <IconReceiptTax className="text-blue-600" size={24} />
                                            {t('sales_points_section_title')}
                                        </h3>
                                        <Badge
                                            color={environment === 'production' ? 'teal' : 'yellow'}
                                            variant="filled"
                                            size="sm"
                                        >
                                            {environment === 'production' ? t('production') : t('homologation')}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {t('sales_points_section_desc')}
                                    </p>
                                </div>
                            </div>

                            {/* Section 1: Previous AFIP Sales Points */}
                            <div className="space-y-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-semibold text-sm text-gray-900">
                                                {t('afip_previous_sales_points_title')}
                                            </h4>
                                            <Badge color="blue" variant="light" size="xs">
                                                {t('env_production_portal')}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            {t('afip_previous_sales_points_desc')}
                                        </p>
                                    </div>
                                    <Button
                                        variant="light"
                                        color="blue"
                                        size="xs"
                                        radius="xl"
                                        leftSection={<IconRefresh size={14} />}
                                        onClick={handleSyncAfipSalesPoints}
                                        loading={syncSalesPointsMutation.isPending}
                                        disabled={isPending}
                                    >
                                        {t('sync_afip_sales_points_btn')}
                                    </Button>
                                </div>

                                {environment === 'development' && (
                                    <Alert
                                        variant="light"
                                        color="blue"
                                        title={t('homologation_pos_notice_title')}
                                        icon={<IconInfoCircle size={16} />}
                                        className="text-xs"
                                    >
                                        {t('homologation_pos_notice_desc')}
                                    </Alert>
                                )}

                                {(!afipSettings.all_sales_points || afipSettings.all_sales_points.length === 0) ? (
                                    <div className="p-6 border border-dashed border-gray-300 rounded-lg text-center bg-gray-50">
                                        <p className="text-xs text-gray-500">{t('no_afip_points_loaded')}</p>
                                    </div>
                                ) : (
                                    <div className="overflow-hidden border border-slate-200 rounded-md">
                                        <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
                                            <Table.Thead className="bg-slate-50 border-b border-slate-200">
                                                <Table.Tr>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">POS</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('sales_point_name')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('sales_point_system')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('status_in_afip')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600 text-right">{t('actions')}</Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                                {afipSettings.all_sales_points.map((p: any, idx: number) => {
                                                    const isConfigured = salesPoints.some(sp => sp.number === String(p.number));
                                                    const isWs = isWebServicePos(p.system);
                                                    return (
                                                        <Table.Tr key={p.number || idx}>
                                                            <Table.Td>
                                                                <div className='whitespace-nowrap solid-box-border-dark text-xxs font-bold'>
                                                                    {p.number}
                                                                </div>
                                                            </Table.Td>
                                                            <Table.Td>
                                                                <div className="font-semibold text-xs text-slate-800">
                                                                    {p.name || p.displayName}
                                                                </div>
                                                                {p.address && <div className="text-[10px] text-gray-400">{p.address}</div>}
                                                            </Table.Td>
                                                            <Table.Td>
                                                                <span className="text-xs text-slate-600 font-mono">
                                                                    {p.system}
                                                                </span>
                                                            </Table.Td>
                                                            <Table.Td>
                                                                {p.deactivated ? (
                                                                    <Badge color="red" variant="dot" size="xs">{t('deactivated')}</Badge>
                                                                ) : p.blocked ? (
                                                                    <Badge color="orange" variant="dot" size="xs">{t('blocked')}</Badge>
                                                                ) : (
                                                                    <Badge color="green" variant="dot" size="xs">{t('active')}</Badge>
                                                                )}
                                                            </Table.Td>
                                                            <Table.Td className="text-right">
                                                                {isConfigured ? (
                                                                    <Badge color="teal" variant="light" size="xs" leftSection={<IconCheck size={10} />}>
                                                                        {t('already_configured_in_turizi')}
                                                                    </Badge>
                                                                ) : !isWs ? (
                                                                    <Badge color="gray" variant="light" size="xs">
                                                                        {t('requires_web_services')}
                                                                    </Badge>
                                                                ) : (
                                                                    <Button
                                                                        size="xs"
                                                                        variant="light"
                                                                        color="green"
                                                                        onClick={() => handleImportAfipPoint(p)}
                                                                        disabled={isPending || p.deactivated || p.blocked}
                                                                    >
                                                                        {t('use')}
                                                                    </Button>
                                                                )}
                                                            </Table.Td>
                                                        </Table.Tr>
                                                    );
                                                })}
                                            </Table.Tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>

                            {/* Section 2: Active Turizi Sales Points */}
                            <div className="space-y-3 pt-4 border-t border-gray-100">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-semibold text-sm text-gray-900">
                                            {t('configured_sales_points')} ({salesPoints.length})
                                        </h4>
                                        <Badge
                                            color={environment === 'production' ? 'teal' : 'yellow'}
                                            variant="light"
                                            size="xs"
                                        >
                                            {environment === 'production' ? t('production') : t('homologation')}
                                        </Badge>
                                    </div>
                                    <Button
                                        variant="light"
                                        color="black"
                                        size="xs"
                                        radius="xl"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={handleOpenCreateSalesPoint}
                                        disabled={isPending || !afipSettings.service_authorized}
                                    >
                                        {t('add_sales_point')}
                                    </Button>
                                </div>

                                {salesPoints.length === 0 ? (
                                    <div className="p-6 border border-dashed border-gray-300 rounded-lg text-center bg-gray-50">
                                        <div className="text-xs text-gray-500">
                                            {t('no_sales_points_configured_hint')}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="overflow-hidden border border-slate-200 rounded-md">
                                        <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
                                            <Table.Thead className="bg-slate-50 border-b border-slate-200">
                                                <Table.Tr>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">POS #</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('sales_point_name')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('sales_point_system')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('origin_label')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600">{t('environment')}</Table.Th>
                                                    <Table.Th className="text-xs uppercase font-bold text-gray-600 text-right">{t('actions')}</Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>
                                            <Table.Tbody>
                                                {salesPoints.map((sp, idx) => (
                                                    <Table.Tr key={sp.id || sp.number || idx}>
                                                        <Table.Td>
                                                            <Badge color="blue" variant="light" size="sm">
                                                                POS {sp.number}
                                                            </Badge>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <div className="font-semibold text-xs text-slate-800">{sp.name}</div>
                                                            {sp.address && <div className="text-[10px] text-gray-400">{sp.address}</div>}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <span className="text-xs text-slate-600 font-mono">
                                                                {sp.system}
                                                            </span>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {sp.origin === 'afip' ? (
                                                                <Badge color="cyan" variant="outline" size="xs">
                                                                    {t('origin_afip')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge color="violet" variant="outline" size="xs">
                                                                    {t('origin_user')}
                                                                </Badge>
                                                            )}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Badge
                                                                color={environment === 'production' ? 'teal' : 'yellow'}
                                                                variant="dot"
                                                                size="xs"
                                                            >
                                                                {environment === 'production' ? t('production') : t('homologation')}
                                                            </Badge>
                                                        </Table.Td>
                                                        <Table.Td className="text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <ActionIcon
                                                                    variant="subtle"
                                                                    color="gray"
                                                                    size="sm"
                                                                    title={t('edit_sales_point')}
                                                                    onClick={() => handleOpenEditSalesPoint(sp)}
                                                                    disabled={isPending}
                                                                >
                                                                    <IconEdit size={16} />
                                                                </ActionIcon>
                                                                <ActionIcon
                                                                    variant="subtle"
                                                                    color="red"
                                                                    size="sm"
                                                                    title={t('delete_sales_point')}
                                                                    onClick={() => handleDeleteSalesPoint(sp)}
                                                                    disabled={isPending}
                                                                >
                                                                    <IconTrash size={16} />
                                                                </ActionIcon>
                                                            </div>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                ))}
                                            </Table.Tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </SettingsLayout>
            ) : (
                <div className="p-8 border border-dashed border-gray-200 rounded-lg text-center bg-gray-50">
                    <p className="text-gray-500">{t('no_additional_attributes')}</p>
                </div>
            )}

            {/* Error Detail Modal */}
            <CardModal
                opened={errorModalOpened}
                onClose={() => setErrorModalOpened(false)}
                header={
                    <div className="text-lg font-semibold text-red-600 flex items-center gap-2">
                        <IconAlertCircle size={20} />
                        {t('afip_error_detail')}
                    </div>
                }
                size="md"
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                        {t('afip_error_desc')}
                    </p>
                    <pre className="bg-gray-950 text-gray-100 p-4 rounded-lg overflow-auto text-xs max-h-[300px] border border-gray-800 shadow-inner leading-relaxed select-all">
                        {JSON.stringify(errorData, null, 2)}
                    </pre>
                    <div className="flex justify-end mt-2">
                        <Button variant="light" color="gray" onClick={() => setErrorModalOpened(false)}>
                            {t('close')}
                        </Button>
                    </div>
                </div>
            </CardModal>

            {/* Certificate Detail Modal */}
            <CardModal
                opened={certModalOpened}
                onClose={() => setCertModalOpened(false)}
                header={
                    <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <IconReceiptTax className="text-blue-600" size={20} />
                        {t('certificate_modal_title')}
                    </div>
                }
                size="md"
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                        {t('certificate_modal_desc')}
                    </p>
                    <pre className="bg-gray-950 text-gray-100 p-4 rounded-lg overflow-auto text-xs max-h-[300px] border border-gray-800 shadow-inner leading-relaxed select-all">
                        {afipSettings.cert}
                    </pre>
                    <div className="flex justify-between items-center mt-2">
                        <Button
                            variant="outline"
                            color="blue"
                            onClick={handleCopyCert}
                        >
                            {t('copy_certificate')}
                        </Button>
                        <Button
                            variant="light"
                            color="gray"
                            onClick={() => setCertModalOpened(false)}
                        >
                            {t('close')}
                        </Button>
                    </div>
                </div>
            </CardModal>

            {/* Edit Sales Point Modal */}
            <CardModal
                opened={salesPointModalOpened}
                onClose={() => {
                    setSalesPointModalOpened(false);
                    setEditingPos(null);
                }}
                header={
                    <div className="font-bold text-sm text-slate-900">
                        {editingPos ? t('edit_sales_point') : t('create_afip_sales_point_modal_title')}
                    </div>
                }
                size="fit"
            >
                <div className="space-y-4">
                    {!editingPos && (
                        <p className="text-xs text-gray-500">
                            {t('create_afip_sales_point_modal_desc')}
                        </p>
                    )}
                    {editingPos && (
                        <TextInput
                            label={t('sales_point_number')}
                            value={modalPosNumber}
                            disabled
                        />
                    )}
                    <TextInput
                        label={t('sales_point_name')}
                        placeholder="e.g. Mi Empresa, Sucursal Centro"
                        value={modalPosName}
                        onChange={(e) => setModalPosName(e.target.value)}
                        required
                    />
                    <Autocomplete
                        label={t('sales_point_address')}
                        placeholder={t('sales_point_address_placeholder')}
                        description={t('sales_point_address_hint')}
                        value={modalPosAddress}
                        onChange={setModalPosAddress}
                        data={availableAddresses}
                    />
                    <Select
                        label={t('sales_point_system')}
                        value={modalPosSystem}
                        disabled={Boolean(editingPos)}
                        onChange={(val) => setModalPosSystem(val || 'MAW')}
                        data={[
                            { value: 'MAW', label: 'Factura Electrónica - Monotributo - Web Services (MAW)' },
                            { value: 'RAW', label: 'RECE - Factura Electrónica - Web Services (RAW)' },
                            { value: 'FEEWS', label: 'Exportación - Web Services (FEEWS)' }
                        ]}
                    />
                    <div className="flex gap-2 justify-end pt-2">
                        <Button
                            variant="default"
                            size="xs"
                            onClick={() => {
                                setSalesPointModalOpened(false);
                                setEditingPos(null);
                            }}
                            disabled={createSalesPointMutation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            loading={createSalesPointMutation.isPending}
                            disabled={!modalPosName.trim()}
                            onClick={editingPos ? handleSaveSalesPointModal : handleCreateSalesPoint}
                        >
                            {editingPos ? t('save') : t('create_in_afip_btn')}
                        </Button>
                    </div>
                </div>
            </CardModal>
        </div>
    );
}
